/**
 * @module utils/validation
 * @description Input validation utilities for Preparo
 *
 * Provides comprehensive validation for all data models:
 * - Projects
 * - Documents
 * - Images
 * - Content
 * - Files (for import)
 *
 * @example
 * import { validateProject, validateDocument, validateFile } from '@utils/validation';
 *
 * const projectResult = validateProject({ title: 'My Book' });
 * if (!projectResult.valid) {
 *   console.error(projectResult.errors);
 * }
 */

import { files as filesConfig } from "../config";
import type {
  CreateProjectInput,
  CreateDocumentInput,
  UpdateProjectInput,
  UpdateDocumentInput,
  CreateImageInput,
  CreateTemplateInput,
  DocumentStatus,
  DocumentSection,
  DocumentType,
  TemplateType,
} from "../types";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** Validation result */
export interface ValidationResult {
  /** Whether validation passed */
  valid: boolean;
  /** List of validation errors */
  errors: ValidationError[];
  /** List of validation warnings (non-blocking) */
  warnings: ValidationWarning[];
}

/** Single validation error */
export interface ValidationError {
  /** Field that failed validation */
  field: string;
  /** Error code for programmatic handling */
  code: ValidationErrorCode;
  /** Human-readable error message */
  message: string;
  /** Expected value/format (if applicable) */
  expected?: string;
  /** Actual value received */
  received?: unknown;
}

/** Single validation warning */
export interface ValidationWarning {
  /** Field with warning */
  field: string;
  /** Warning code */
  code: string;
  /** Human-readable warning message */
  message: string;
}

/** Validation error codes */
export type ValidationErrorCode =
  | "REQUIRED"
  | "MIN_LENGTH"
  | "MAX_LENGTH"
  | "INVALID_FORMAT"
  | "INVALID_TYPE"
  | "INVALID_VALUE"
  | "INVALID_ID"
  | "INVALID_DATE"
  | "INVALID_STATUS"
  | "INVALID_DOCUMENT_TYPE"
  | "INVALID_DOCUMENT_SECTION"
  | "FILE_TOO_LARGE"
  | "UNSUPPORTED_FORMAT"
  | "UNSUPPORTED_MIME_TYPE"
  | "INVALID_DATA_URL"
  | "CONTENT_TOO_LARGE";

// ============================================================================
// CONSTANTS
// ============================================================================

/** Valid document statuses */
const VALID_STATUSES: DocumentStatus[] = ["todo", "in-progress", "done"];

/** Valid document sections */
const VALID_SECTIONS: DocumentSection[] = [
  "frontmatter",
  "bodymatter",
  "backmatter",
];

/** Valid document types by section */
const VALID_DOCUMENT_TYPES: Record<DocumentSection, DocumentType[]> = {
  frontmatter: [
    "titlepage",
    "halftitlepage",
    "copyright-page",
    "dedication",
    "prologue",
    "abstract",
    "acknowledgments",
    "credits",
    "other-credits",
    "keywords",
    "imprint",
    "contributors",
    "errata",
    "revision-history",
    "seriespage",
    "foreword",
    "preface",
    "frontispiece",
    "introduction",
  ],
  bodymatter: ["chapter", "part"],
  backmatter: [
    "appendix",
    "colophon",
    "bibliography",
    "index",
    "conclusion",
    "annexe",
    "postface",
  ],
};

/** All valid document types */
const ALL_DOCUMENT_TYPES: DocumentType[] = [
  ...VALID_DOCUMENT_TYPES.frontmatter,
  ...VALID_DOCUMENT_TYPES.bodymatter,
  ...VALID_DOCUMENT_TYPES.backmatter,
];

/** UUID v4 regex pattern */
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** ISO 8601 date regex pattern */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;

/** Data URL regex pattern */
const DATA_URL_PATTERN = /^data:([a-zA-Z0-9]+\/[a-zA-Z0-9+.-]+);base64,/;

/** Allowed image MIME types */
const ALLOWED_IMAGE_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/svg+xml",
];

/** Maximum content length (1MB) */
const MAX_CONTENT_LENGTH = 1_000_000;

/** Maximum title length */
const MAX_TITLE_LENGTH = 500;

/** Maximum description length */
const MAX_DESCRIPTION_LENGTH = 2000;

/** Maximum metadata length */
const MAX_METADATA_LENGTH = 50000;

/** Maximum image size (10MB) */
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;

/** Maximum template size (5MB) */
const MAX_TEMPLATE_SIZE = 5 * 1024 * 1024;

/** Valid template types */
const VALID_TEMPLATE_TYPES: TemplateType[] = ["css", "font", "preparo", "lua", "csl", "indesign", "config", "other"];

/** Allowed template MIME types */
const ALLOWED_TEMPLATE_MIME_TYPES = [
  "text/css",
  "text/markdown",
  "font/ttf",
  "font/otf",
  "font/woff",
  "font/woff2",
  "application/x-font-ttf",
  "application/x-font-otf",
  "application/font-woff",
  "application/font-woff2",
  "text/plain",
  "text/x-lua",
  "application/x-lua",
  "application/xml",
  "text/xml",
  "application/vnd.citationstyles.style+xml",
  "application/octet-stream",
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create empty validation result
 */
function createResult(): ValidationResult {
  return { valid: true, errors: [], warnings: [] };
}

/**
 * Add error to validation result
 */
function addError(
  result: ValidationResult,
  field: string,
  code: ValidationErrorCode,
  message: string,
  expected?: string,
  received?: unknown,
): void {
  result.valid = false;
  result.errors.push({ field, code, message, expected, received });
}

/**
 * Add warning to validation result
 */
function addWarning(
  result: ValidationResult,
  field: string,
  code: string,
  message: string,
): void {
  result.warnings.push({ field, code, message });
}

/**
 * Check if value is a non-empty string
 */
function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * Check if value is a valid UUID v4
 */
function isValidUUID(value: unknown): boolean {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

/**
 * Check if value is a valid ISO 8601 date string
 */
function isValidISODate(value: unknown): boolean {
  if (typeof value !== "string") return false;
  if (!ISO_DATE_PATTERN.test(value)) return false;
  const date = new Date(value);
  return !isNaN(date.getTime());
}

/**
 * Check if value is a valid data URL
 */
function isValidDataURL(value: unknown): boolean {
  return typeof value === "string" && DATA_URL_PATTERN.test(value);
}

/**
 * Sanitize string input (trim whitespace)
 */
export function sanitizeString(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.trim();
}

// ============================================================================
// PROJECT VALIDATION
// ============================================================================

/**
 * Validate project creation input
 */
export function validateCreateProject(input: unknown): ValidationResult {
  const result = createResult();

  if (!input || typeof input !== "object") {
    addError(result, "input", "INVALID_TYPE", "Input must be an object");
    return result;
  }

  const data = input as CreateProjectInput;

  // Title is required
  if (!isNonEmptyString(data.title)) {
    addError(result, "title", "REQUIRED", "Project title is required");
  } else if (data.title.length > MAX_TITLE_LENGTH) {
    addError(
      result,
      "title",
      "MAX_LENGTH",
      `Title must be at most ${MAX_TITLE_LENGTH} characters`,
      `<= ${MAX_TITLE_LENGTH}`,
      data.title.length,
    );
  }

  // Description is optional but has max length
  if (data.description !== undefined && data.description !== null) {
    if (typeof data.description !== "string") {
      addError(
        result,
        "description",
        "INVALID_TYPE",
        "Description must be a string",
      );
    } else if (data.description.length > MAX_DESCRIPTION_LENGTH) {
      addError(
        result,
        "description",
        "MAX_LENGTH",
        `Description must be at most ${MAX_DESCRIPTION_LENGTH} characters`,
        `<= ${MAX_DESCRIPTION_LENGTH}`,
        data.description.length,
      );
    }
  }

  // Author is optional but has max length
  if (data.author !== undefined && data.author !== null) {
    if (typeof data.author !== "string") {
      addError(result, "author", "INVALID_TYPE", "Author must be a string");
    } else if (data.author.length > MAX_TITLE_LENGTH) {
      addError(
        result,
        "author",
        "MAX_LENGTH",
        `Author must be at most ${MAX_TITLE_LENGTH} characters`,
        `<= ${MAX_TITLE_LENGTH}`,
        data.author.length,
      );
    }
  }

  // Metadata is optional but has max length
  if (data.metadata !== undefined && data.metadata !== null) {
    if (typeof data.metadata !== "string") {
      addError(result, "metadata", "INVALID_TYPE", "Metadata must be a string");
    } else if (data.metadata.length > MAX_METADATA_LENGTH) {
      addError(
        result,
        "metadata",
        "MAX_LENGTH",
        `Metadata must be at most ${MAX_METADATA_LENGTH} characters`,
        `<= ${MAX_METADATA_LENGTH}`,
        data.metadata.length,
      );
    }
  }

  return result;
}

/**
 * Validate project update input
 */
export function validateUpdateProject(input: unknown): ValidationResult {
  const result = createResult();

  if (!input || typeof input !== "object") {
    addError(result, "input", "INVALID_TYPE", "Input must be an object");
    return result;
  }

  const data = input as UpdateProjectInput;

  // Title is optional on update but must be valid if provided
  if (data.title !== undefined) {
    if (!isNonEmptyString(data.title)) {
      addError(result, "title", "REQUIRED", "Title cannot be empty");
    } else if (data.title.length > MAX_TITLE_LENGTH) {
      addError(
        result,
        "title",
        "MAX_LENGTH",
        `Title must be at most ${MAX_TITLE_LENGTH} characters`,
        `<= ${MAX_TITLE_LENGTH}`,
        data.title.length,
      );
    }
  }

  // Description validation (same as create)
  if (data.description !== undefined && data.description !== null) {
    if (typeof data.description !== "string") {
      addError(
        result,
        "description",
        "INVALID_TYPE",
        "Description must be a string",
      );
    } else if (data.description.length > MAX_DESCRIPTION_LENGTH) {
      addError(
        result,
        "description",
        "MAX_LENGTH",
        `Description must be at most ${MAX_DESCRIPTION_LENGTH} characters`,
      );
    }
  }

  // Author validation
  if (data.author !== undefined && data.author !== null) {
    if (typeof data.author !== "string") {
      addError(result, "author", "INVALID_TYPE", "Author must be a string");
    } else if (data.author.length > MAX_TITLE_LENGTH) {
      addError(
        result,
        "author",
        "MAX_LENGTH",
        `Author must be at most ${MAX_TITLE_LENGTH} characters`,
      );
    }
  }

  // Metadata validation
  if (data.metadata !== undefined && data.metadata !== null) {
    if (typeof data.metadata !== "string") {
      addError(result, "metadata", "INVALID_TYPE", "Metadata must be a string");
    } else if (data.metadata.length > MAX_METADATA_LENGTH) {
      addError(
        result,
        "metadata",
        "MAX_LENGTH",
        `Metadata must be at most ${MAX_METADATA_LENGTH} characters`,
      );
    }
  }

  return result;
}

// ============================================================================
// DOCUMENT VALIDATION
// ============================================================================

/**
 * Validate document creation input
 */
export function validateCreateDocument(input: unknown): ValidationResult {
  const result = createResult();

  if (!input || typeof input !== "object") {
    addError(result, "input", "INVALID_TYPE", "Input must be an object");
    return result;
  }

  const data = input as CreateDocumentInput;

  // Project ID is required and must be valid UUID
  if (!isNonEmptyString(data.projectId)) {
    addError(result, "projectId", "REQUIRED", "Project ID is required");
  } else if (!isValidUUID(data.projectId)) {
    addError(
      result,
      "projectId",
      "INVALID_ID",
      "Project ID must be a valid UUID",
    );
  }

  // Title is required
  if (!isNonEmptyString(data.title)) {
    addError(result, "title", "REQUIRED", "Document title is required");
  } else if (data.title.length > MAX_TITLE_LENGTH) {
    addError(
      result,
      "title",
      "MAX_LENGTH",
      `Title must be at most ${MAX_TITLE_LENGTH} characters`,
    );
  }

  // Status is optional but must be valid if provided
  if (data.status !== undefined && data.status !== null) {
    if (!VALID_STATUSES.includes(data.status)) {
      addError(
        result,
        "status",
        "INVALID_STATUS",
        `Status must be one of: ${VALID_STATUSES.join(", ")}`,
        VALID_STATUSES.join(", "),
        data.status,
      );
    }
  }

  // Document type is optional but must be valid if provided
  if (data.documentType !== undefined && data.documentType !== null) {
    if (!ALL_DOCUMENT_TYPES.includes(data.documentType)) {
      addError(
        result,
        "documentType",
        "INVALID_DOCUMENT_TYPE",
        "Invalid document type",
        ALL_DOCUMENT_TYPES.join(", "),
        data.documentType,
      );
    }
  }

  // Document section is optional but must be valid if provided
  if (data.documentSection !== undefined && data.documentSection !== null) {
    if (!VALID_SECTIONS.includes(data.documentSection)) {
      addError(
        result,
        "documentSection",
        "INVALID_DOCUMENT_SECTION",
        `Section must be one of: ${VALID_SECTIONS.join(", ")}`,
        VALID_SECTIONS.join(", "),
        data.documentSection,
      );
    }
  }

  // Content is optional but has max length
  if (data.content !== undefined && data.content !== null) {
    if (typeof data.content !== "string") {
      addError(result, "content", "INVALID_TYPE", "Content must be a string");
    } else if (data.content.length > MAX_CONTENT_LENGTH) {
      addError(
        result,
        "content",
        "CONTENT_TOO_LARGE",
        `Content must be at most ${MAX_CONTENT_LENGTH} characters`,
      );
      addWarning(
        result,
        "content",
        "LARGE_CONTENT",
        "Very large content may impact performance",
      );
    }
  }

  // Order must be a non-negative number if provided
  if (data.order !== undefined && data.order !== null) {
    if (
      typeof data.order !== "number" ||
      data.order < 0 ||
      !Number.isInteger(data.order)
    ) {
      addError(
        result,
        "order",
        "INVALID_TYPE",
        "Order must be a non-negative integer",
      );
    }
  }

  return result;
}

/**
 * Validate document update input
 */
export function validateUpdateDocument(input: unknown): ValidationResult {
  const result = createResult();

  if (!input || typeof input !== "object") {
    addError(result, "input", "INVALID_TYPE", "Input must be an object");
    return result;
  }

  const data = input as UpdateDocumentInput;

  // Title is optional on update but must be valid if provided
  if (data.title !== undefined) {
    if (!isNonEmptyString(data.title)) {
      addError(result, "title", "REQUIRED", "Title cannot be empty");
    } else if (data.title.length > MAX_TITLE_LENGTH) {
      addError(
        result,
        "title",
        "MAX_LENGTH",
        `Title must be at most ${MAX_TITLE_LENGTH} characters`,
      );
    }
  }

  // Status validation
  if (data.status !== undefined && data.status !== null) {
    if (!VALID_STATUSES.includes(data.status)) {
      addError(
        result,
        "status",
        "INVALID_STATUS",
        `Status must be one of: ${VALID_STATUSES.join(", ")}`,
      );
    }
  }

  // Document type validation
  if (data.documentType !== undefined && data.documentType !== null) {
    if (!ALL_DOCUMENT_TYPES.includes(data.documentType)) {
      addError(
        result,
        "documentType",
        "INVALID_DOCUMENT_TYPE",
        "Invalid document type",
      );
    }
  }

  // Document section validation
  if (data.documentSection !== undefined && data.documentSection !== null) {
    if (!VALID_SECTIONS.includes(data.documentSection)) {
      addError(
        result,
        "documentSection",
        "INVALID_DOCUMENT_SECTION",
        `Section must be one of: ${VALID_SECTIONS.join(", ")}`,
      );
    }
  }

  // Content validation
  if (data.content !== undefined && data.content !== null) {
    if (typeof data.content !== "string") {
      addError(result, "content", "INVALID_TYPE", "Content must be a string");
    } else if (data.content.length > MAX_CONTENT_LENGTH) {
      addError(
        result,
        "content",
        "CONTENT_TOO_LARGE",
        `Content must be at most ${MAX_CONTENT_LENGTH} characters`,
      );
    }
  }

  return result;
}

// ============================================================================
// IMAGE VALIDATION
// ============================================================================

/**
 * Validate image creation input
 */
export function validateCreateImage(input: unknown): ValidationResult {
  const result = createResult();

  if (!input || typeof input !== "object") {
    addError(result, "input", "INVALID_TYPE", "Input must be an object");
    return result;
  }

  const data = input as CreateImageInput;

  // Project ID is required
  if (!isNonEmptyString(data.projectId)) {
    addError(result, "projectId", "REQUIRED", "Project ID is required");
  } else if (!isValidUUID(data.projectId)) {
    addError(
      result,
      "projectId",
      "INVALID_ID",
      "Project ID must be a valid UUID",
    );
  }

  // Filename is required
  if (!isNonEmptyString(data.filename)) {
    addError(result, "filename", "REQUIRED", "Filename is required");
  } else if (data.filename.length > 255) {
    addError(
      result,
      "filename",
      "MAX_LENGTH",
      "Filename must be at most 255 characters",
    );
  }

  // MIME type is required and must be valid
  if (!isNonEmptyString(data.mimeType)) {
    addError(result, "mimeType", "REQUIRED", "MIME type is required");
  } else if (!ALLOWED_IMAGE_MIME_TYPES.includes(data.mimeType)) {
    addError(
      result,
      "mimeType",
      "UNSUPPORTED_MIME_TYPE",
      `MIME type must be one of: ${ALLOWED_IMAGE_MIME_TYPES.join(", ")}`,
      ALLOWED_IMAGE_MIME_TYPES.join(", "),
      data.mimeType,
    );
  }

  // Data URL is required and must be valid format
  if (!isNonEmptyString(data.dataUrl)) {
    addError(result, "dataUrl", "REQUIRED", "Data URL is required");
  } else if (!isValidDataURL(data.dataUrl)) {
    addError(result, "dataUrl", "INVALID_DATA_URL", "Invalid data URL format");
  }

  // Size is required and must be valid
  if (data.size === undefined || data.size === null) {
    addError(result, "size", "REQUIRED", "File size is required");
  } else if (typeof data.size !== "number" || data.size < 0) {
    addError(result, "size", "INVALID_TYPE", "Size must be a positive number");
  } else if (data.size > MAX_IMAGE_SIZE) {
    addError(
      result,
      "size",
      "FILE_TOO_LARGE",
      `Image must be at most ${MAX_IMAGE_SIZE / (1024 * 1024)}MB`,
      `<= ${MAX_IMAGE_SIZE}`,
      data.size,
    );
  }

  // Width/height are optional but must be valid if provided
  if (data.width !== undefined && data.width !== null) {
    if (
      typeof data.width !== "number" ||
      data.width <= 0 ||
      !Number.isInteger(data.width)
    ) {
      addError(
        result,
        "width",
        "INVALID_TYPE",
        "Width must be a positive integer",
      );
    }
  }

  if (data.height !== undefined && data.height !== null) {
    if (
      typeof data.height !== "number" ||
      data.height <= 0 ||
      !Number.isInteger(data.height)
    ) {
      addError(
        result,
        "height",
        "INVALID_TYPE",
        "Height must be a positive integer",
      );
    }
  }

  return result;
}

// ============================================================================
// TEMPLATE VALIDATION
// ============================================================================

/**
 * Validate template creation input
 */
export function validateCreateTemplate(input: unknown): ValidationResult {
  const result = createResult();

  if (!input || typeof input !== "object") {
    addError(result, "input", "INVALID_TYPE", "Input must be an object");
    return result;
  }

  const data = input as CreateTemplateInput;

  // Project ID or Preset ID is required (at least one must be a valid UUID)
  const hasProjectId = isNonEmptyString(data.projectId) && isValidUUID(data.projectId);
  const hasPresetId = isNonEmptyString(data.presetId) && isValidUUID(data.presetId);
  if (!hasProjectId && !hasPresetId) {
    addError(result, "projectId", "REQUIRED", "Project ID or Preset ID is required");
  }

  // Filename is required
  if (!isNonEmptyString(data.filename)) {
    addError(result, "filename", "REQUIRED", "Filename is required");
  } else if (data.filename.length > 255) {
    addError(
      result,
      "filename",
      "MAX_LENGTH",
      "Filename must be at most 255 characters",
    );
  }

  // Template type is required and must be valid
  if (!isNonEmptyString(data.templateType)) {
    addError(result, "templateType", "REQUIRED", "Template type is required");
  } else if (
    !VALID_TEMPLATE_TYPES.includes(data.templateType as TemplateType)
  ) {
    addError(
      result,
      "templateType",
      "INVALID_TYPE",
      `Template type must be one of: ${VALID_TEMPLATE_TYPES.join(", ")}`,
      VALID_TEMPLATE_TYPES.join(", "),
      data.templateType,
    );
  }

  // MIME type is required
  if (!isNonEmptyString(data.mimeType)) {
    addError(result, "mimeType", "REQUIRED", "MIME type is required");
  } else if (!ALLOWED_TEMPLATE_MIME_TYPES.includes(data.mimeType)) {
    // Just warn, don't reject - MIME types can vary
    addWarning(
      result,
      "mimeType",
      "UNKNOWN_MIME_TYPE",
      `Unexpected MIME type: ${data.mimeType}. File will be processed based on template type.`,
    );
  }

  // Content is required
  if (!isNonEmptyString(data.content)) {
    addError(result, "content", "REQUIRED", "Content is required");
  }

  // Size is required and must be valid
  if (data.size === undefined || data.size === null) {
    addError(result, "size", "REQUIRED", "File size is required");
  } else if (typeof data.size !== "number" || data.size < 0) {
    addError(result, "size", "INVALID_TYPE", "Size must be a positive number");
  } else if (data.size > MAX_TEMPLATE_SIZE) {
    addError(
      result,
      "size",
      "FILE_TOO_LARGE",
      `Template must be at most ${MAX_TEMPLATE_SIZE / (1024 * 1024)}MB`,
      `<= ${MAX_TEMPLATE_SIZE}`,
      data.size,
    );
  }

  // Description is optional but has max length
  if (data.description !== undefined && data.description !== null) {
    if (typeof data.description !== "string") {
      addError(
        result,
        "description",
        "INVALID_TYPE",
        "Description must be a string",
      );
    } else if (data.description.length > MAX_DESCRIPTION_LENGTH) {
      addError(
        result,
        "description",
        "MAX_LENGTH",
        `Description must be at most ${MAX_DESCRIPTION_LENGTH} characters`,
      );
    }
  }

  return result;
}

// ============================================================================
// FILE VALIDATION (for import)
// ============================================================================

/**
 * Validate file for import (comprehensive validation)
 */
export function validateFileForImport(file: File | null): ValidationResult {
  const result = createResult();

  if (!file) {
    addError(result, "file", "REQUIRED", "No file provided");
    return result;
  }

  // Check file extension
  const fileName = file.name.toLowerCase();
  const extension = "." + fileName.split(".").pop();

  if (!filesConfig.allowedExtensions.includes(extension)) {
    addError(
      result,
      "extension",
      "UNSUPPORTED_FORMAT",
      `Unsupported file format: ${extension}`,
      filesConfig.allowedExtensions.join(", "),
      extension,
    );
  }

  // Check file size
  if (file.size > filesConfig.maxSize) {
    addError(
      result,
      "size",
      "FILE_TOO_LARGE",
      `File is too large: ${(file.size / (1024 * 1024)).toFixed(2)}MB (max: ${filesConfig.maxSize / (1024 * 1024)}MB)`,
      `<= ${filesConfig.maxSize}`,
      file.size,
    );
  } else if (file.size > filesConfig.warningSizeThreshold) {
    addWarning(
      result,
      "size",
      "LARGE_FILE",
      "Large file may take longer to process",
    );
  }

  // Check MIME type if available
  if (file.type && !filesConfig.allowedMimeTypes.includes(file.type)) {
    // Only warn, don't error - MIME types can be unreliable
    addWarning(
      result,
      "mimeType",
      "UNKNOWN_MIME_TYPE",
      `Unexpected MIME type: ${file.type}. File will be processed based on extension.`,
    );
  }

  // Check filename length
  if (file.name.length > filesConfig.maxFilenameLength) {
    addWarning(
      result,
      "filename",
      "LONG_FILENAME",
      "Very long filename may be truncated",
    );
  }

  return result;
}

/**
 * Validate content for saving
 */
export function validateContent(content: unknown): ValidationResult {
  const result = createResult();

  if (content === undefined || content === null) {
    // Empty content is allowed
    return result;
  }

  if (typeof content !== "string") {
    addError(result, "content", "INVALID_TYPE", "Content must be a string");
    return result;
  }

  if (content.length > MAX_CONTENT_LENGTH) {
    addError(
      result,
      "content",
      "CONTENT_TOO_LARGE",
      `Content exceeds maximum length of ${MAX_CONTENT_LENGTH} characters`,
      `<= ${MAX_CONTENT_LENGTH}`,
      content.length,
    );
  }

  return result;
}

/**
 * Validate UUID
 */
export function validateUUID(
  value: unknown,
  fieldName = "id",
): ValidationResult {
  const result = createResult();

  if (!isNonEmptyString(value)) {
    addError(result, fieldName, "REQUIRED", `${fieldName} is required`);
  } else if (!isValidUUID(value)) {
    addError(
      result,
      fieldName,
      "INVALID_ID",
      `${fieldName} must be a valid UUID`,
    );
  }

  return result;
}

// ============================================================================
// UTILITY EXPORTS
// ============================================================================

export {
  isValidUUID,
  isValidISODate,
  isValidDataURL,
  VALID_STATUSES,
  VALID_SECTIONS,
  ALL_DOCUMENT_TYPES,
  VALID_DOCUMENT_TYPES,
  VALID_TEMPLATE_TYPES,
  MAX_CONTENT_LENGTH,
  MAX_TITLE_LENGTH,
  MAX_IMAGE_SIZE,
  MAX_TEMPLATE_SIZE,
};
